/**
 * Renders the scrollable message thread with markdown for assistant replies.
 */
import React from 'react';
import ReactMarkdown from 'react-markdown';
import SafeMarkdownImage from './SafeMarkdownImage';
import SafeMarkdownLink from './SafeMarkdownLink';
import { deptCitationKind, linkDeptCitations } from '../../utils/deptCitations';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { Box, Button, Chip, Paper, Typography, CircularProgress, IconButton, Tooltip } from '@mui/material';
import ShareOutlined from '@mui/icons-material/ShareOutlined';
import ThumbUpOutlined from '@mui/icons-material/ThumbUpOutlined';
import ThumbUpAlt from '@mui/icons-material/ThumbUpAlt';
import ThumbDownOutlined from '@mui/icons-material/ThumbDownOutlined';
import ThumbDownAlt from '@mui/icons-material/ThumbDownAlt';
import { useTheme } from '@mui/material/styles';
import { brand } from '../../theme/interlinkedTheme';
import { unwrapMarkdownFence } from '../../utils/unwrapMarkdownFence';
// Per-tool card registry (plan 05 B3, 2026-09-29).
import ConfirmationCardView from './confirmationCards';
// Tool-result provenance citations (plan 05 C7, 2026-09-29).
import EvidenceSources from './EvidenceSources';

/**
 * Scrollable list of user/assistant/error bubbles plus a loading indicator.
 *
 * @param {object} props
 * @param {Array} props.messages - Thread messages from useChatApp.
 * @param {boolean} props.isLoading - True while waiting for the agent reply.
 * @param {React.RefObject} props.messagesEndRef - Scroll anchor at the bottom.
 * @param {(confirmationId: string) => void} props.onConfirmReport - Approves a
 *   pending confirmation card (see ./confirmationCards / utils/confirmationCards.js).
 * @param {(messageId: string, value: 1|-1) => void} [props.onRate] - Rates an
 *   answer that carries the turn's record id (docs/guardian/03 §1.4).
 * @param {(message: object) => void} [props.onShare] - Shares an answer that
 *   carries a record id to the department board (WS-F, docs/guardian/06).
 */
export default function ChatMessageList({ messages, isLoading, messagesEndRef, onConfirmReport, onRate, onShare }) {
  const theme = useTheme();

  return (
    <>
      {messages.map((message) => {
        if (message.type === 'confirmation') {
          return (
            <Box key={message.id} sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
              <ConfirmationCardView message={message} isLoading={isLoading} onConfirm={onConfirmReport} />
            </Box>
          );
        }
        return (
        <Box
          key={message.id}
          sx={{
            display: 'flex',
            justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start',
            mb: 2,
          }}
        >
          <Paper
            className={message.type === 'user' ? 'il-user-bubble' : 'il-assistant-bubble'}
            sx={{
              p: 2,
              maxWidth: '70%',
              borderRadius: message.type === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
              border: message.type === 'error' ? '1px solid' : undefined,
              borderColor: message.type === 'error' ? 'error.main' : undefined,
              color: message.type === 'user' ? 'primary.contrastText' : 'text.primary',
              ...(message.type === 'user' ? {} : { bgcolor: 'background.paper' }),
            }}
          >
            {message.type === 'assistant' ? (
              <ReactMarkdown
                className="markdown-content"
                remarkPlugins={[remarkGfm, remarkBreaks]}
                components={{
                  // Never auto-load a remote image from agent text (pass 20).
                  img: SafeMarkdownImage,
                  // Only absolute https links are clickable, with the host shown
                  // (2026-09-25 gap closure).
                  a: ({ href, children }) => {
                    // Department citations (D1 wave 3) render as chips.
                    const kind = deptCitationKind(href);
                    if (kind) {
                      return (
                        <Chip
                          component="span"
                          size="small"
                          variant="outlined"
                          label={kind === 'rule' ? 'Department rule' : 'Department SOP'}
                          sx={{ mx: 0.25, height: 20, fontSize: '0.7rem', verticalAlign: 'baseline' }}
                        />
                      );
                    }
                    return <SafeMarkdownLink href={href}>{children}</SafeMarkdownLink>;
                  },
                  table: ({ children }) => (
                    <Box sx={{ overflowX: 'auto', my: 1 }}>
                      <table
                        style={{
                          width: '100%',
                          borderCollapse: 'collapse',
                          fontSize: '0.875rem',
                        }}
                      >
                        {children}
                      </table>
                    </Box>
                  ),
                  th: ({ children }) => (
                    <th
                      style={{
                        border: `1px solid ${theme.palette.divider}`,
                        padding: '8px 12px',
                        backgroundColor:
                          theme.palette.mode === 'dark'
                            ? theme.palette.background.paper
                            : theme.palette.secondary.main,
                        color: theme.palette.text.primary,
                        textAlign: 'left',
                        fontWeight: 'bold',
                      }}
                    >
                      {children}
                    </th>
                  ),
                  td: ({ children }) => (
                    <td
                      style={{
                        border: `1px solid ${theme.palette.divider}`,
                        padding: '8px 12px',
                        backgroundColor: theme.palette.background.paper,
                        color: theme.palette.text.primary,
                      }}
                    >
                      {children}
                    </td>
                  ),
                  h1: ({ children }) => (
                    <Typography variant="h5" sx={{ color: brand.navy, mb: 1, mt: 2 }}>
                      {children}
                    </Typography>
                  ),
                  h2: ({ children }) => (
                    <Typography variant="h6" sx={{ color: 'text.primary', mb: 1, mt: 2 }}>
                      {children}
                    </Typography>
                  ),
                  h3: ({ children }) => (
                    <Typography variant="subtitle1" sx={{ color: 'text.secondary', mb: 1, mt: 1 }}>
                      {children}
                    </Typography>
                  ),
                  code: ({ children, className }) => {
                    const isInline = !className;
                    return isInline ? (
                      <code>{children}</code>
                    ) : (
                      <Box
                        component="pre"
                        sx={{
                          bgcolor: 'action.hover',
                          color: 'text.primary',
                          padding: 2,
                          borderRadius: 1,
                          overflow: 'auto',
                          fontSize: '0.875rem',
                          fontFamily: 'var(--il-font-mono)',
                          my: 1,
                        }}
                      >
                        <code>{children}</code>
                      </Box>
                    );
                  },
                  p: ({ children }) => (
                    <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
                      {children}
                    </Typography>
                  ),
                  strong: ({ children }) => (
                    <strong style={{ color: brand.navy }}>{children}</strong>
                  ),
                  em: ({ children }) => (
                    <em style={{ color: theme.palette.text.secondary }}>{children}</em>
                  ),
                  br: () => <br />,
                }}
              >
                {linkDeptCitations(unwrapMarkdownFence(message.content))}
              </ReactMarkdown>
            ) : (
              <Typography
                variant="body1"
                sx={{
                  whiteSpace: 'pre-wrap',
                  color: message.type === 'user' ? 'white' : 'text.primary',
                }}
              >
                {message.content}
              </Typography>
            )}
            {message.type === 'assistant' && message.sources ? (
              <EvidenceSources sources={message.sources} />
            ) : null}
            {message.type === 'assistant' && message.recordId && (onRate || onShare) ? (
              <AnswerActions message={message} onRate={onRate} onShare={onShare} />
            ) : null}
            <Typography
              variant="caption"
              sx={{
                color: message.type === 'user' ? 'rgba(255,255,255,0.7)' : 'text.secondary',
                display: 'block',
                mt: 1,
              }}
            >
              {message.timestamp}
            </Typography>
          </Paper>
        </Box>
        );
      })}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
          <Paper
            className="il-assistant-bubble"
            sx={{
              p: 2,
              borderRadius: '18px 18px 18px 4px',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <CircularProgress size={16} sx={{ color: 'primary.main' }} />
            <Typography variant="body2" sx={{ color: 'text.primary' }}>
              Thinking...
            </Typography>
          </Paper>
        </Box>
      )}
      <div ref={messagesEndRef} />
    </>
  );
}

/**
 * Per-answer actions: the rating thumbs (when `onRate`) and a "Share to
 * department" button (when `onShare`, i.e. the user is a department member).
 */
function AnswerActions({ message, onRate, onShare }) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, mt: 1 }}>
      {onRate && <AnswerRating message={message} onRate={onRate} />}
      {onShare && (
        <Button
          size="small"
          startIcon={<ShareOutlined fontSize="inherit" />}
          onClick={() => onShare(message)}
          data-testid="share-to-department"
        >
          Share to department
        </Button>
      )}
    </Box>
  );
}

/** Thumbs for one answer; the active thumb is filled and aria-pressed. */
function AnswerRating({ message, onRate }) {
  const up = message.rating === 1;
  const down = message.rating === -1;
  return (
    <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }} data-testid="answer-rating">
      <Tooltip title="Helpful">
        <IconButton
          size="small"
          aria-label="Helpful"
          aria-pressed={up}
          onClick={() => onRate(message.id, 1)}
        >
          {up ? <ThumbUpAlt fontSize="inherit" /> : <ThumbUpOutlined fontSize="inherit" />}
        </IconButton>
      </Tooltip>
      <Tooltip title="Not helpful">
        <IconButton
          size="small"
          aria-label="Not helpful"
          aria-pressed={down}
          onClick={() => onRate(message.id, -1)}
        >
          {down ? <ThumbDownAlt fontSize="inherit" /> : <ThumbDownOutlined fontSize="inherit" />}
        </IconButton>
      </Tooltip>
    </Box>
  );
}
